/**
 * Gắn kết quả kiểm tra lên bản xem lại toàn phiếu (Doc/11a): mỗi phần còn bao nhiêu mục thiếu, bao
 * nhiêu cảnh báo. Logic thuần, không DOM, để kiểm được trong môi trường `node` của vitest.
 *
 * `outline` lấy từ `surveyOutline` của @farmer/audit-rules, `findings` từ cùng lượt kiểm tra với
 * bảng cảnh báo, nên mục lục, bản xem lại và nút Nộp không thể nói ba điều khác nhau.
 */
import { CONSTRAINT_CODE_PREFIX, isCompletenessCode } from '@farmer/audit-rules';

function collectKeys(items, out) {
  for (const item of items) {
    if (item.kind === 'question') {
      out.add(item.key);
    } else if (item.kind === 'group') {
      collectKeys(item.items, out);
    } else {
      // Lỗi "vượt số dòng tối đa" trỏ vào chính nhóm lặp, không vào ô nào.
      out.add(item.key);
      for (const row of item.rows) {
        row.cells.forEach((c) => out.add(c.key));
        collectKeys(row.items, out);
      }
    }
  }
  return out;
}

/** `id` phần chứa khoá đó. Khoá ngoài bộ câu hỏi đang hiện (lớp master data, câu ẩn) thì `undefined`. */
export function sectionIndex(outline) {
  const index = new Map();
  for (const section of outline) {
    for (const key of collectKeys(section.items, new Set())) index.set(key, section.id);
  }
  return index;
}

/**
 * Số mục thiếu và cảnh báo theo từng phần. Chỉ `REQ:` / `CONS:` là "thiếu" (chặn nộp); luật A/B là
 * cảnh báo dù mức đỏ, vì chúng không chặn. Một finding chỉ tính một lần, vào phần của khoá đầu tiên
 * nằm trong phiếu đang hiện.
 *
 * @returns {Map<string|null, {missing: number, warnings: number}>} đủ mọi phần của `outline`
 */
export function sectionStates(outline, findings) {
  const index = sectionIndex(outline);
  const states = new Map(outline.map((s) => [s.id, { missing: 0, warnings: 0 }]));
  for (const f of findings) {
    const key = (f.keys || []).find((k) => index.has(k));
    if (key === undefined) continue;
    const state = states.get(index.get(key));
    if (isCompletenessCode(f.code || '')) state.missing++;
    else state.warnings++;
  }
  return states;
}

/** Danh sách mục chặn nộp, theo thứ tự trong phiếu (`checkCompleteness` duyệt đúng thứ tự đó). */
export function blockingItems(outline, findings) {
  const index = sectionIndex(outline);
  return findings
    .filter((f) => isCompletenessCode(f.code || ''))
    .map((f) => ({ key: f.keys?.[0], title: f.title, sectionId: index.get(f.keys?.[0]) ?? null }));
}

/** Khoá → 'missing' | 'invalid', để bản xem lại ghi đỏ đúng ô. */
export function problemByKey(findings) {
  const out = new Map();
  for (const f of findings) {
    if (!isCompletenessCode(f.code || '')) continue;
    const kind = String(f.code).startsWith(`${CONSTRAINT_CODE_PREFIX}:`) ? 'invalid' : 'missing';
    (f.keys || []).forEach((k) => out.set(k, kind));
  }
  return out;
}

/** Huy hiệu của phần trong bản xem lại. */
export function sectionBadge({ missing, warnings }) {
  if (missing) return { text: `${missing} chưa đủ`, tone: 'rejected' };
  if (warnings) return { text: `${warnings} cảnh báo`, tone: 'warn' };
  return { text: 'Đủ', tone: 'approved' };
}
